import { Component, signal, inject, OnInit, isDevMode } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TokenWatcherService } from './core/services/token-watcher.service';
import { VersionService } from './core/services/version.service';
import { SessionExpiryDialogComponent } from './core/components/session-expiry-dialog/session-expiry-dialog.component';
import { DevPermissionsPanelComponent } from './core/components/dev-permissions-panel/dev-permissions-panel.component';
import { AuthService } from './services/auth.service';
import { PermissionsService } from './services/permissions.service';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';

/** Cada cuanto pregunta la proyeccion si hay version nueva. */
const REVISION_PROYECCION_MS = 10 * 60_000;

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, SessionExpiryDialogComponent, DevPermissionsPanelComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  protected readonly title = signal('BELATZ_APP');

  private readonly tokenWatcher = inject(TokenWatcherService);
  private readonly authSvc      = inject(AuthService);
  private readonly permSvc      = inject(PermissionsService);
  private readonly swUpdate     = inject(SwUpdate);
  private readonly versionSvc   = inject(VersionService);

  ngOnInit(): void {
    // Deja lista la version propia y empieza a comparar contra el servidor y el API.
    this.versionSvc.iniciar();

    // La pantalla proyectada lleva su propia sesion y se renueva sola: ahi no aplican el aviso de
    // sesion por vencer ni el bloqueo por inactividad, y no usa el menu ni sus permisos.
    const proyeccion = location.pathname.includes('/asistencia/proyeccion');

    if (!proyeccion && this.authSvc.isAuthenticated()) {
      this.tokenWatcher.start();
      if (!this.permSvc.isLoaded()) {
        this.permSvc.loadPermissions().subscribe();
      }
    }

    this.authSvc.tokenRenewed$.subscribe(() => {
      if (!proyeccion) this.tokenWatcher.start();
    });

    // Version nueva desplegada. Solo la proyeccion se recarga sola: no captura nada y conserva su
    // sesion. Las demas pestanas siguen con lo que el usuario estaba capturando y el pie le ofrece
    // recargar cuando le convenga.
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
      ).subscribe(() => {
        if (proyeccion) this.swUpdate.activateUpdate().then(() => window.location.reload());
      });

      // El worker revisa la version al abrir una pantalla, y la proyeccion puede pasar dias sin
      // abrir ninguna: se le pregunta cada cierto tiempo.
      if (proyeccion) {
        setInterval(() => this.swUpdate.checkForUpdate().catch(() => false), REVISION_PROYECCION_MS);
      }

      // Si el navegador ya no tiene los archivos de la version con que se abrio la pestana, las
      // pantallas que falten por cargar no abririan: solo queda recargar.
      this.swUpdate.unrecoverable.subscribe(() => window.location.reload());
    }
  }
}
